import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, Users, Package, Activity, LogOut, ShieldCheck,
  Inbox, Settings, Warehouse, UserCog, CalendarDays, Layers,
  FileBarChart, FileText, Clock, DollarSign, Boxes, Search,
  Keyboard, UsersRound, ClipboardList, ChevronDown, ChevronRight, Menu
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { CommandPalette } from '@/components/CommandPalette';
import { ShortcutsHelp } from '@/components/ShortcutsHelp';
import NotificationBell from '@/components/NotificationBell';

const STERISUL_LOGO = "/sterisul-logo.png";

// Access control matrix: which profiles can see each menu item
const ACCESS_MAP = {
  '/dashboard':     ['admin', 'administrativo', 'enfermeiro', 'tecnico', 'financeiro', 'visualizador'],
  '/recepcao':      ['admin', 'enfermeiro', 'tecnico'],
  '/montagem':      ['admin', 'enfermeiro', 'tecnico'],
  '/ciclos':        ['admin', 'enfermeiro', 'tecnico'],
  '/arsenal':       ['admin', 'enfermeiro', 'tecnico'],
  '/relatorio-ciclos': ['admin', 'enfermeiro', 'tecnico', 'administrativo', 'visualizador'],
  '/clientes':      ['admin', 'administrativo', 'enfermeiro', 'financeiro'],
  '/agendamentos':  ['admin', 'administrativo', 'enfermeiro'],
  '/solicitacoes':  ['admin', 'administrativo', 'enfermeiro'],
  '/materiais':     ['admin', 'enfermeiro', 'tecnico'],
  '/autoclaves':    ['admin', 'enfermeiro'],
  '/pop':           ['admin', 'enfermeiro', 'tecnico', 'visualizador'],
  '/financeiro':    ['admin', 'administrativo', 'financeiro'],
  '/estoque':       ['admin', 'administrativo', 'enfermeiro'],
  '/relatorios':    ['admin', 'administrativo', 'enfermeiro', 'financeiro', 'visualizador'],
  '/rh':            ['admin', 'administrativo'],
  '/usuarios':      ['admin', 'administrativo'],
  '/scheduler':     ['admin'],
  '/configuracoes': ['admin', 'administrativo'],
};

// Menu structure grouped by sections
const MENU_SECTIONS = [
  {
    id: 'overview',
    label: 'VISÃO GERAL',
    items: [
      { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', shortcut: 'Alt+D' },
    ]
  },
  {
    id: 'operational',
    label: 'FLUXO OPERACIONAL',
    items: [
      { path: '/recepcao', icon: Inbox, label: 'Recepção', shortcut: 'Alt+R' },
      { path: '/montagem', icon: Layers, label: 'Montagem', shortcut: 'Alt+M' },
      { path: '/ciclos', icon: Activity, label: 'Ciclos', shortcut: 'Alt+C' },
      { path: '/arsenal', icon: Warehouse, label: 'Arsenal', shortcut: 'Alt+A' },
      { path: '/relatorio-ciclos', icon: FileBarChart, label: 'Rel. Ciclos' },
    ]
  },
  {
    id: 'clients',
    label: 'CLIENTES',
    items: [
      { path: '/clientes', icon: Users, label: 'Clientes', shortcut: 'Alt+L' },
      { path: '/agendamentos', icon: CalendarDays, label: 'Agendamentos', shortcut: 'Alt+G' },
      { path: '/solicitacoes', icon: ClipboardList, label: 'Solicitações' },
    ]
  },
  {
    id: 'cadastros',
    label: 'CADASTROS',
    items: [
      { path: '/materiais', icon: Package, label: 'Materiais', shortcut: 'Alt+T' },
      { path: '/autoclaves', icon: ShieldCheck, label: 'Autoclaves', shortcut: 'Alt+U' },
      { path: '/pop', icon: FileText, label: 'POPs', shortcut: 'Alt+P' },
    ]
  },
  {
    id: 'admin',
    label: 'ADMINISTRATIVO',
    items: [
      { path: '/financeiro', icon: DollarSign, label: 'Financeiro', shortcut: 'Alt+F' },
      { path: '/estoque', icon: Boxes, label: 'Estoque', shortcut: 'Alt+E' },
      { path: '/relatorios', icon: FileBarChart, label: 'Relatórios', shortcut: 'Alt+O' },
      { path: '/rh', icon: UsersRound, label: 'RH', shortcut: 'Alt+H' },
    ]
  },
  {
    id: 'system',
    label: 'SISTEMA',
    items: [
      { path: '/usuarios', icon: UserCog, label: 'Usuários' },
      { path: '/scheduler', icon: Clock, label: 'Agendador' },
      { path: '/configuracoes', icon: Settings, label: 'Configurações' },
    ]
  },
];

function canAccess(path, perfil) {
  const allowed = ACCESS_MAP[path];
  if (!allowed) return true;
  return allowed.includes(perfil || 'admin');
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const perfil = user?.perfil || user?.role || 'admin';
  
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  useKeyboardShortcuts({
    onSearch: () => setShowCommandPalette(true),
    onHelp: () => setShowShortcutsHelp(true),
    onClose: () => {
      setShowCommandPalette(false);
      setShowShortcutsHelp(false);
    },
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Translate perfil to display name
  const perfilDisplay = {
    admin: 'Administrador',
    administrativo: 'Administrativo',
    enfermeiro: 'Enfermeiro',
    tecnico: 'Técnico',
    financeiro: 'Financeiro',
    visualizador: 'Visualizador',
  }[perfil] || perfil;

  const sidebarNav = (
    <nav className="p-3 space-y-1">
      {MENU_SECTIONS.map((section) => {
        // Filter items by user profile
        const visibleItems = section.items.filter(item => canAccess(item.path, perfil));
        if (visibleItems.length === 0) return null;

        const isCollapsed = collapsedSections[section.id];
        const hasActiveItem = visibleItems.some(item => location.pathname === item.path);

        return (
          <div key={section.id} className="mb-1">
            {/* Section header */}
            <button
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-600 transition-colors"
              data-testid={`section-${section.id}`}
            >
              <span>{section.label}</span>
              {isCollapsed ? (
                <ChevronRight className="w-3 h-3" />
              ) : (
                <ChevronDown className="w-3 h-3" />
              )}
            </button>

            {/* Section items */}
            {!isCollapsed && (
              <div className="space-y-0.5">
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      onClick={() => { setMobileMenuOpen(false); navigate(item.path); }}
                      title={item.shortcut || ''}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md transition-colors duration-150 ${
                        isActive
                          ? 'bg-blue-50 text-blue-600 font-medium'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm flex-1 text-left">{item.label}</span>
                      {item.shortcut && (
                        <kbd className="hidden lg:inline text-[10px] px-1.5 py-0.5 bg-slate-100 rounded text-slate-400">
                          {item.shortcut.replace('Alt+', '⌥')}
                        </kbd>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="lg:hidden shrink-0" onClick={() => setMobileMenuOpen(true)} aria-label="Abrir menu" data-testid="btn-mobile-menu">
              <Menu className="w-5 h-5" />
            </Button>
            <img src={STERISUL_LOGO} alt="Sterisul" className="w-8 h-8 sm:w-10 sm:h-10 object-contain shrink-0" />
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Sterisul
              </h1>
              <p className="text-xs text-slate-500 hidden lg:block">Centro de Esterilização</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-4 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCommandPalette(true)}
              className="hidden sm:flex items-center gap-2 text-slate-500"
            >
              <Search className="w-4 h-4" />
              <span className="text-xs">Buscar...</span>
              <kbd className="ml-2 px-1.5 py-0.5 text-[10px] bg-slate-100 rounded border">Ctrl+K</kbd>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowShortcutsHelp(true)}
              className="text-slate-500 hidden md:inline-flex"
              title="Atalhos de Teclado (Ctrl+H)"
            >
              <Keyboard className="w-4 h-4" />
            </Button>
            <NotificationBell />
            <div className="text-right hidden md:block">
              <div className="text-sm font-medium text-slate-900">{user?.name}</div>
              <div className="text-xs text-slate-500">{perfilDisplay}</div>
            </div>
            <Button data-testid="btn-logout" onClick={handleLogout} variant="outline" size="sm">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:block w-64 shrink-0 bg-white border-r border-slate-200 min-h-[calc(100vh-73px)] overflow-y-auto">
          {sidebarNav}
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 p-3 sm:p-6 lg:p-8" data-testid="app-main">
          <Outlet />
        </main>
      </div>

      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-72 p-0 overflow-y-auto" data-testid="mobile-menu">
          <SheetHeader className="px-4 py-3 border-b text-left">
            <SheetTitle className="flex items-center gap-2">
              <img src={STERISUL_LOGO} alt="" className="w-7 h-7 object-contain" /> Sterisul
            </SheetTitle>
            <p className="text-xs text-slate-500">{user?.name} · {perfilDisplay}</p>
          </SheetHeader>
          {sidebarNav}
        </SheetContent>
      </Sheet>

      <CommandPalette open={showCommandPalette} onOpenChange={setShowCommandPalette} />
      <ShortcutsHelp open={showShortcutsHelp} onOpenChange={setShowShortcutsHelp} />
    </div>
  );
}
